import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * 동적 스플래시 캠페인 — cache-ahead 모델.
 *
 * 콜드 스타트에서 네트워크를 기다리면 스플래시가 늦게 떠서 의미가 없으므로,
 * "이번 실행에서 다음 실행 것을 미리 받아 둔다":
 *  - 표시: 이전 세션이 localStorage에 저장해 둔 캠페인을 즉시 렌더
 *    (이미지도 이전 세션에 HTTP 캐시로 프리로드됨 — UUID 파일명이라 URL 불변)
 *  - 갱신: 부팅이 끝난 뒤 백그라운드로 최신 활성 캠페인을 받아 캐시 교체
 * 첫 설치 첫 실행은 캐시가 없어 기본 로고 스플래시가 뜬다(의도된 동작).
 */

export const SPLASH_CACHE_KEY = "locarora-splash-campaign-v1";
export const SPLASH_SESSION_SHOWN_KEY = "locarora-splash-shown";

export interface SplashCampaign {
  id: string;
  image_url: string;
  background_color: string;
  starts_at: string | null;
  ends_at: string | null;
}

interface CachedSplash {
  version: 1;
  campaign: SplashCampaign;
  fetchedAt: number;
}

/**
 * 이전 세션이 캐시한 캠페인을 읽는다.
 * 캐시 시점 이후 기간이 만료됐을 수 있으므로 노출 기간을 반드시 재검증한다.
 */
export function readCachedCampaign(): SplashCampaign | null {
  try {
    const raw = localStorage.getItem(SPLASH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSplash;
    if (parsed.version !== 1 || !parsed.campaign?.image_url) return null;

    const now = Date.now();
    const { starts_at, ends_at } = parsed.campaign;
    if (starts_at && new Date(starts_at).getTime() > now) return null;
    if (ends_at && new Date(ends_at).getTime() <= now) return null;

    return parsed.campaign;
  } catch {
    return null;
  }
}

/**
 * 다음 콜드 스타트를 위해 최신 활성 캠페인을 받아 캐시한다.
 * RLS가 활성+기간 내 행만 반환하며, 기간이 겹치면 시작일이 늦은 캠페인이 우선.
 * 모든 실패는 조용히 무시 — 스플래시는 있으면 좋은 기능이지 앱을 막으면 안 된다.
 */
export async function refreshCampaignCache(
  supabase: SupabaseClient,
): Promise<void> {
  try {
    const { data, error } = await supabase
      .from("splash_campaigns")
      .select("id,image_url,background_color,starts_at,ends_at")
      .order("starts_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) return;

    if (!data) {
      localStorage.removeItem(SPLASH_CACHE_KEY);
      return;
    }

    // 이미지를 WebView HTTP 캐시에 미리 적재 (cacheControl 31536000 + 불변 URL)
    const img = new Image();
    img.src = data.image_url;

    const cached: CachedSplash = {
      version: 1,
      campaign: data as SplashCampaign,
      fetchedAt: Date.now(),
    };
    localStorage.setItem(SPLASH_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // silent — 다음 실행에서 재시도
  }
}
